import { useEffect, useMemo } from 'react';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import { setSelectedCapacityTeams } from '../../../../store/process-analysis-store/process-analysis-store.actions';
import {
  useComparisonMode,
  useFilterWasTouched,
} from '../../../../store/process-analysis-store/process-analysis-store.hooks';
import { ComparisonMode, Tab } from '../../process-analysis.type';

/**
 * Hook to handle auto-selection of capacity teams when filters haven't been touched by the user
 *
 * @returns {void}
 */
const useAutoSelectFilters = (): void => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const comparisonMode = useComparisonMode();
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const capacityTeamsWasTouched = useFilterWasTouched('selectedCapacityTeams');

  const teams = portfolio?.projects || [];
  const teamIds = useMemo(() => teams.map((team) => team.id), [teams]);

  useEffect(() => {
    if (!portfolio?.id) return;

    if (teamIds.length === 0) {
      return setSelectedCapacityTeams([]);
    }

    if (capacityTeamsWasTouched) return;

    const isPortfolioTab = activeTab === Tab.Portfolios;
    const isTeamsTab = activeTab === Tab.Teams;
    const isSingleMode = comparisonMode === ComparisonMode.Single;
    const isMultipleMode = comparisonMode === ComparisonMode.Multiple;

    const shouldAutoSelectForSingleMode = isSingleMode && isPortfolioTab;
    const shouldAutoSelectForMultipleMode = isMultipleMode && isTeamsTab;

    if (shouldAutoSelectForSingleMode || shouldAutoSelectForMultipleMode) {
      setSelectedCapacityTeams(teamIds);
    }
  }, [portfolio?.id, teamIds, comparisonMode, activeTab, capacityTeamsWasTouched]);
};

export { useAutoSelectFilters };
